import React, { useState, useEffect } from 'react';
import { AlertTriangle, LogOut, Terminal } from 'lucide-react';
import { Button } from '@/components/common/ui/Button';
import { useClerk } from '@clerk/clerk-react'; 

const SessionTimeoutModal: React.FC = () => {
    const { signOut } = useClerk(); 
    const [isExpired, setIsExpired] = useState(false);
    const [message, setMessage] = useState('SESSION TIMEOUT. PLEASE SIGN IN AGAIN.');

    useEffect(() => {
        const handleSessionExpiry = (event: any) => {
            setIsExpired(true);
            if (event.detail?.message) {
                setMessage(event.detail.message.toUpperCase());
            }
        };

        window.addEventListener('session-expired', handleSessionExpiry);
        return () => window.removeEventListener('session-expired', handleSessionExpiry);
    }, []);

    const handleAcknowledgeAndLogout = async () => {
        try {
            // 1. Structural Kill: Revoke Clerk session state on server-side first
            await signOut();
        } catch (err) {
            console.error('SERVER_REVOCATION_BYPASS:', err);
        } finally {
            // 2. Clear frontend secondary cache layers
            localStorage.removeItem('auth_token');
            localStorage.removeItem('mfa_verified');
            sessionStorage.clear();

            // 3. Clean window intercept redirect directly to auth path route
            // FIX: Pointing to /auth/login explicitly to match sub-routed login module view bounds
            window.location.href = '/auth/login';
        }
    };

    if (!isExpired) return null;

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md font-mono select-none">
            <div className="bg-[#0A0B0E] border border-[#FF3333]/40 shadow-[0_0_50px_rgba(255,51,51,0.2)] rounded-2xl w-full max-w-[420px] p-8 flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                
                {/* Top indicator track */}
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3333] to-transparent shadow-[0_0_10px_#FF3333]"></div>

                <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-16 h-16 bg-black border border-[#FF3333]/30 rounded-xl flex items-center justify-center mb-5 relative shadow-inner">
                        <div className="absolute inset-0 rounded-xl border border-[#FF3333]/20 animate-ping opacity-30 pointer-events-none"></div>
                        <AlertTriangle className="w-7 h-7 text-[#FF3333] drop-shadow-[0_0_8px_rgba(255,51,51,0.5)]" />
                    </div>
                    
                    <h2 className="text-[15px] font-black text-white tracking-[0.25em] uppercase">
                        Session Expired
                    </h2>
                    
                    <div className="w-full bg-black/40 border border-[#1A1D24] rounded-lg p-3 mt-4">
                        <p className="text-[11px] text-[#FF3333] font-bold tracking-wide uppercase leading-relaxed">
                            {message}
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    <Button
                        onClick={handleAcknowledgeAndLogout}
                        className="w-full bg-[#FF3333] text-white font-black h-11 text-[11px] tracking-[0.2em] rounded-lg shadow-[0_0_20px_rgba(255,51,51,0.2)] hover:bg-[#cc0000] active:scale-95 transition-all uppercase"
                        leftIcon={<LogOut className="w-4 h-4" />}
                        autoFocus
                    >
                        Sign In
                    </Button>
                    
                    <div className="flex items-center justify-center gap-1.5 text-[8px] font-bold text-slate-600 uppercase tracking-widest pt-1">
                        <Terminal className="w-3 h-3" /> Token_Invalidation_Sequence_Ready
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionTimeoutModal;